import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Image,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  Upload,
  message,
} from "antd";
import { PlusOutlined, UploadOutlined, DeleteOutlined, EditOutlined, CheckOutlined, ArrowUpOutlined, ArrowDownOutlined, PictureOutlined, FilePdfOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import {
  apiGetClubSite,
  apiSaveClubSite,
  apiGetClubHomepage,
  apiSaveClubHomepage,
  apiDeleteClubHomepage,
  apiReorderClubHomepage,
  apiGetSubdomainFiles,
} from "services/customWebpageService";

const { Title, Text } = Typography;
const { TextArea } = Input;

// Shared across every image upload on this page — all route through the same
// backend fileUpload() helper (BaseController.php), which allows
// gif/jpg/jpeg/png/heic/heif and rejects anything over 20MB.
const IMAGE_UPLOAD_HINT = "Accepted formats: JPG, PNG, GIF, HEIC · Max size: 20MB";
// Toolbar "Image Page" content also accepts PDF, unlike every other image field on this page.
const IMAGE_OR_PDF_UPLOAD_HINT = "Accepted formats: JPG, PNG, GIF, HEIC, PDF · Max size: 20MB";

const isPdf = (fileOrUrl) => {
  if (!fileOrUrl) return false;
  if (fileOrUrl instanceof File || fileOrUrl instanceof Blob) {
    return fileOrUrl.type === "application/pdf";
  }
  return typeof fileOrUrl === "string" && fileOrUrl.toLowerCase().split("?")[0].endsWith(".pdf");
};

const CustomWebpagePage = () => {
  const [siteForm] = Form.useForm();
  const [sectionForm] = Form.useForm();
  const userDataStore = useSelector((x) => x.userDataStore);
  const appDataStore = useSelector((x) => x.appDataStore);
  const clubId = appDataStore?.club?.id;

  const [siteLoading, setSiteLoading] = useState(false);
  const [siteInfoPage, setSiteInfoPage] = useState(false);
  const [siteInfoPageFile, setSiteInfoPageFile] = useState("");
  const [siteAvailableFiles, setSiteAvailableFiles] = useState([]);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [logoLeftFile, setLogoLeftFile] = useState(null);
  const [logoRightFile, setLogoRightFile] = useState(null);
  const [logoLeftPreview, setLogoLeftPreview] = useState("");
  const [logoRightPreview, setLogoRightPreview] = useState("");
  const [logoLeftRemoved, setLogoLeftRemoved] = useState(false);
  const [logoRightRemoved, setLogoRightRemoved] = useState(false);
  const [logoLeftUrl, setLogoLeftUrl] = useState("");
  const [logoRightUrl, setLogoRightUrl] = useState("");
  const [defaultEmailLogo, setDefaultEmailLogo] = useState(""); // "left", "right", or ""
  const [sectionModalVisible, setSectionModalVisible] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionImageFile, setSectionImageFile] = useState(null);
  const [infoPageEnabled, setInfoPageEnabled] = useState(false);
  const [infoPageContent, setInfoPageContent] = useState("");
  const [infoPageType, setInfoPageType] = useState("custom");
  const [infoPageFile, setInfoPageFile] = useState("");
  const [externalUrlEnabled, setExternalUrlEnabled] = useState(false);
  const [externalUrl, setExternalUrl] = useState("");
  const [availableFiles, setAvailableFiles] = useState([]);
  const [toolbarEnabled, setToolbarEnabled] = useState(false);
  const [toolbarItems, setToolbarItems] = useState([]); // [{id, imageFile, imagePreview, pageFile}]
  const [homepageType, setHomepageType] = useState("sections"); // "sections" | "single"
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState("");
  const [homepageText, setHomepageText] = useState("");

  const toAbsoluteUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const base = process.env.REACT_APP_API?.replace("/api/", "") || "";
    return `${base}/${url}`;
  };

  const disabledSiteActions = useMemo(() => !userDataStore?.token || !clubId, [userDataStore?.token, clubId]);

  useEffect(() => {
    if (!disabledSiteActions) {
      loadSite();
      loadSections();
    }
  }, [disabledSiteActions]);

  const loadSite = async () => {
    setSiteLoading(true);
    try {
      const res = await apiGetClubSite({ token: userDataStore.token, club_id: clubId });
      if (res) {
        siteForm.setFieldsValue({
          domain: res.domain || "",
          site_title: res.site_title || "",
          site_subtitle: res.site_subtitle || "",
        });
        const infoPageOn = res.info_page === "1" || res.info_page === 1;
        setSiteInfoPage(infoPageOn);
        setSiteInfoPageFile(res.info_page_file || "");
        if (infoPageOn) {
          // pre-load available files so the dropdown is populated
          setTimeout(() => loadSiteAvailableFiles(), 0);
        }
        setLogoLeftPreview(toAbsoluteUrl(res.logo_left) || "");
        setLogoRightPreview(toAbsoluteUrl(res.logo_right) || "");
        setLogoLeftRemoved(false);
        setLogoRightRemoved(false);
        setLogoLeftUrl(res.logo_left_url || "");
        setLogoRightUrl(res.logo_right_url || "");
        setDefaultEmailLogo(res.default_email_logo || "");
        setToolbarEnabled(res.toolbar_enabled === "1" || res.toolbar_enabled === 1);
        setHomepageType(res.homepage_type || "sections");
        setHeroImagePreview(toAbsoluteUrl(res.homepage_hero_image) || "");
        setHomepageText(res.homepage_text || "");
        const parsedToolbar = Array.isArray(res.toolbar_items) ? res.toolbar_items : [];
        setToolbarItems(
          parsedToolbar.map((item, idx) => ({
            id: item.id || String(idx),
            imageFile: null,
            imagePreview: toAbsoluteUrl(item.image_url || item.image || ""),
            pageFile: item.page_file || "",
            name: item.name || "",
            contentType: item.content_type || "html",
            contentImageFile: null,
            contentImagePreview: toAbsoluteUrl(item.content_image || ""),
          }))
        );
        if ((res.toolbar_enabled === "1" || res.toolbar_enabled === 1) && siteAvailableFiles.length === 0) {
          setTimeout(() => loadSiteAvailableFiles(), 0);
        }
      }
    } catch (err) {
      message.error(err || "Unable to load site settings");
    } finally {
      setSiteLoading(false);
    }
  };

  const loadSections = async () => {
    setSectionLoading(true);
    try {
      const res = await apiGetClubHomepage({ token: userDataStore.token, club_id: clubId });
      console.log('Homepage sections loaded:', res);
      setSections(Array.isArray(res) ? res : []);
    } catch (err) {
      message.error(err || "Unable to load homepage sections");
    } finally {
      setSectionLoading(false);
    }
  };

  const handleSaveSite = async () => {
    try {
      const values = await siteForm.validateFields();
      setSiteLoading(true);
      const res = await apiSaveClubSite({
        token: userDataStore.token,
        club_id: clubId,
        domain: values.domain,
        site_title: values.site_title,
        site_subtitle: values.site_subtitle,
        info_page: siteInfoPage ? 1 : 0,
        info_page_file: siteInfoPageFile || "",
        logo_left: logoLeftFile ?? (logoLeftRemoved ? null : logoLeftPreview),
        logo_left_remove: logoLeftRemoved ? 1 : 0,
        logo_left_url: logoLeftUrl,
        logo_right: logoRightFile ?? (logoRightRemoved ? null : logoRightPreview),
        logo_right_remove: logoRightRemoved ? 1 : 0,
        logo_right_url: logoRightUrl,
        default_email_logo: defaultEmailLogo,
        toolbar_enabled: toolbarEnabled ? 1 : 0,
        toolbar_items: toolbarItems,
        homepage_type: homepageType,
        homepage_hero_image: heroImageFile,
        homepage_text: homepageText,
      });
      if (Array.isArray(res?.upload_errors) && res.upload_errors.length > 0) {
        res.upload_errors.forEach((err) => message.error(err, 8));
        message.warning("Website settings saved, but one or more images failed to upload — see errors above.");
      } else {
        message.success("Website settings saved");
      }
      setLogoLeftFile(null);
      setLogoRightFile(null);
      setLogoLeftRemoved(false);
      setLogoRightRemoved(false);
      setHeroImageFile(null);
      loadSite();
    } catch (err) {
      if (err?.errorFields) return; // validation errors already shown
      message.error(err || "Unable to save website settings");
    } finally {
      setSiteLoading(false);
    }
  };

  const resetSectionModal = () => {
    sectionForm.resetFields();
    setSectionImageFile(null);
    setEditingSection(null);
    setInfoPageEnabled(false);
    setInfoPageContent("");
    setInfoPageType("custom");
    setInfoPageFile("");
    setExternalUrlEnabled(false);
    setExternalUrl("");
  };

  const loadAvailableFiles = async () => {
    try {
      const domain = siteForm.getFieldValue("domain");
      if (!domain) return;
      const res = await apiGetSubdomainFiles({ token: userDataStore.token, domain });
      setAvailableFiles(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("Failed to load subdomain files:", err);
      setAvailableFiles([]);
    }
  };

  const loadSiteAvailableFiles = async () => {
    try {
      const domain = siteForm.getFieldValue("domain");
      if (!domain) return;
      const res = await apiGetSubdomainFiles({ token: userDataStore.token, domain, subfolder: "page" });
      setSiteAvailableFiles(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("Failed to load site page files:", err);
      setSiteAvailableFiles([]);
    }
  };

  const addToolbarItem = () => {
    setToolbarItems((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        imageFile: null,
        imagePreview: "",
        pageFile: "",
        name: "",
        contentType: "html",
        contentImageFile: null,
        contentImagePreview: "",
      },
    ]);
  };

  const removeToolbarItem = (idx) => {
    setToolbarItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveToolbarItem = (idx, direction) => {
    setToolbarItems((prev) => {
      const arr = [...prev];
      const swapIdx = idx + direction;
      if (swapIdx < 0 || swapIdx >= arr.length) return arr;
      [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
      return arr;
    });
  };

  const updateToolbarItem = (idx, field, value) => {
    setToolbarItems((prev) => prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  };

  const openNewSection = () => {
    resetSectionModal();
    loadAvailableFiles();
    setSectionModalVisible(true);
  };

  const openEditSection = (record) => {
    setEditingSection(record);
    sectionForm.setFieldsValue({
      title: record.title || "",
      description: record.description || "",
      font_color: record.font_color || "",
    });
    setSectionImageFile(null);
    setInfoPageEnabled(record.info_page_enabled === "1" || record.info_page_enabled === 1);
    setInfoPageContent(record.info_page_content || "");
    setInfoPageType(record.info_page_type || "custom");
    setInfoPageFile(record.info_page_file || "");
    setExternalUrlEnabled(record.external_url_enabled === "1" || record.external_url_enabled === 1);
    setExternalUrl(record.external_url || "");
    loadAvailableFiles();
    setSectionModalVisible(true);
  };

  const handleSaveSection = async () => {
    try {
      const values = await sectionForm.validateFields();
      if (externalUrlEnabled && !externalUrl.trim()) {
        message.error("Please enter a URL, or turn off External URL.");
        return;
      }
      setSectionLoading(true);
      const res = await apiSaveClubHomepage({
        token: userDataStore.token,
        club_id: clubId,
        id: editingSection?.id,
        title: values.title,
        description: values.description,
        font_color: values.font_color,
        image: sectionImageFile,
        info_page_enabled: infoPageEnabled ? 1 : 0,
        info_page_content: infoPageContent,
        info_page_type: infoPageType,
        info_page_file: infoPageFile,
        external_url_enabled: externalUrlEnabled ? 1 : 0,
        external_url: externalUrlEnabled ? externalUrl.trim() : "",
      });
      if (Array.isArray(res?.upload_errors) && res.upload_errors.length > 0) {
        res.upload_errors.forEach((err) => message.error(err, 8));
        message.warning(`${editingSection ? "Section updated" : "Section added"}, but the image failed to upload — see error above.`);
      } else {
        message.success(editingSection ? "Section updated" : "Section added");
      }
      setSectionModalVisible(false);
      resetSectionModal();
      loadSections();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err || "Unable to save section");
    } finally {
      setSectionLoading(false);
    }
  };

  const handleDeleteSection = (record) => {
    Modal.confirm({
      title: "Delete this section?",
      onOk: async () => {
        try {
          setSectionLoading(true);
          await apiDeleteClubHomepage({ token: userDataStore.token, club_id: clubId, id: record.id });
          message.success("Section deleted");
          loadSections();
        } catch (err) {
          message.error(err || "Unable to delete section");
        } finally {
          setSectionLoading(false);
        }
      },
    });
  };

  const moveSectionItem = async (idx, direction) => {
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= sections.length) return;

    const reordered = [...sections];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    setSections(reordered);

    try {
      await apiReorderClubHomepage({
        token: userDataStore.token,
        club_id: clubId,
        ids: reordered.map((item) => item.id),
      });
    } catch (err) {
      message.error(err || "Unable to save new order");
      loadSections(); // revert to server state
    }
  };

  const leftFileList = useMemo(() => {
    if (logoLeftFile) {
      return [{ uid: "left-new", name: logoLeftFile.name, status: "done", url: logoLeftPreview || URL.createObjectURL(logoLeftFile) }];
    }
    if (logoLeftPreview) {
      return [{ uid: "left", name: logoLeftPreview.split("/").pop(), status: "done", url: logoLeftPreview }];
    }
    return [];
  }, [logoLeftFile, logoLeftPreview]);

  const rightFileList = useMemo(() => {
    if (logoRightFile) {
      return [{ uid: "right-new", name: logoRightFile.name, status: "done", url: logoRightPreview || URL.createObjectURL(logoRightFile) }];
    }
    if (logoRightPreview) {
      return [{ uid: "right", name: logoRightPreview.split("/").pop(), status: "done", url: logoRightPreview }];
    }
    return [];
  }, [logoRightFile, logoRightPreview]);

  const siteUploadProps = (setter, previewSetter, fileList) => ({
    beforeUpload: (file) => {
      setter(file);
      previewSetter(URL.createObjectURL(file));
      return false; // prevent auto upload
    },
    onRemove: () => {
      setter(null);
      previewSetter("");
    },
    maxCount: 1,
    accept: "image/*",
    fileList,
    listType: "picture",
    showUploadList: false,
  });

  const sectionColumns = [
    {
      title: "Order",
      key: "order_controls",
      width: 70,
      render: (_, record, idx) => (
        <Space direction="vertical" size={0}>
          <Button
            size="small"
            type="text"
            icon={<ArrowUpOutlined />}
            onClick={() => moveSectionItem(idx, -1)}
            disabled={idx === 0}
          />
          <Button
            size="small"
            type="text"
            icon={<ArrowDownOutlined />}
            onClick={() => moveSectionItem(idx, 1)}
            disabled={idx === sections.length - 1}
          />
        </Space>
      ),
    },
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Description", dataIndex: "description", key: "description", ellipsis: true },
    { title: "Font Color", dataIndex: "font_color", key: "font_color" },
    {
      title: "Info Page",
      dataIndex: "info_page_enabled",
      key: "info_page_enabled",
      width: 100,
      align: "center",
      render: (enabled, record) => {
        if (enabled === "1" || enabled === 1) {
          const type = record.info_page_type === "fixed" ? "Fixed" : "Custom";
          return <span style={{ color: "#52c41a" }}>{type}</span>;
        }
        return "-";
      },
    },
    {
      title: "External URL",
      dataIndex: "external_url_enabled",
      key: "external_url_enabled",
      width: 140,
      align: "center",
      render: (enabled, record) => {
        if (enabled === "1" || enabled === 1) {
          return (
            <a href={record.external_url} target="_blank" rel="noopener noreferrer" title={record.external_url}>
              {record.external_url}
            </a>
          );
        }
        return "-";
      },
    },
    {
      title: "Image",
      dataIndex: "image_url",
      key: "image_url",
      width: 120,
      render: (url, record) => {
        console.log('Image render - url:', url, 'record:', record);
        if (!url) return "-";
        
        // If URL doesn't start with http, prepend the API base URL
        const imageUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_API?.replace('/api/', '')}/${url}`;
        
        return (
          <Image 
            src={imageUrl} 
            alt="section thumbnail"
            preview={{
              mask: "Preview",
            }}
            style={{ 
              width: 80, 
              height: 60, 
              objectFit: "cover",
              borderRadius: 4,
              cursor: "pointer"
            }} 
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
          />
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditSection(record)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteSection(record)} />
        </Space>
      ),
    },
  ];

  if (!clubId) {
    return (
      <Card>
        <Title level={4}>Custom Webpage</Title>
        <Text>Please select a club first.</Text>
      </Card>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Custom Webpage</Title>
      <Text type="secondary">Manage your public Website and Homepage blocks.</Text>
      <Divider />

      <Card title="Website Configuration" loading={siteLoading} style={{ marginBottom: 24 }}>
        <Form layout="vertical" form={siteForm}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Domain"
                name="domain"
                rules={[{ required: true, message: "Domain is required" }]}
              >
                <Input placeholder="e.g. myclub.beclutch.club" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Website Title"
                name="site_title"
                rules={[{ required: true, message: "Website title is required" }]}
              >
                <Input placeholder="Website title" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Website Subtitle" name="site_subtitle">
                <Input placeholder="Optional subtitle" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label=" " colon={false}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={siteInfoPage}
                    onChange={(e) => {
                      setSiteInfoPage(e.target.checked);
                      if (e.target.checked) loadSiteAvailableFiles();
                      else setSiteInfoPageFile("");
                    }}
                  >
                    Info Page Only
                  </Checkbox>
                  {siteInfoPage && (
                    <Select
                      value={siteInfoPageFile || undefined}
                      onChange={(value) => setSiteInfoPageFile(value || "")}
                      placeholder="Select HTML page"
                      style={{ marginLeft: 12, flex: 1 }}
                      allowClear
                      onClear={() => setSiteInfoPageFile("")}
                    >
                      {siteAvailableFiles.map((file) => (
                        <Select.Option key={file} value={file}>
                          {file}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Left Logo">
                <Upload
                  {...siteUploadProps(setLogoLeftFile, setLogoLeftPreview, leftFileList)}
                >
                  <Button icon={<UploadOutlined />}>Select Left Logo</Button>
                </Upload>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>{IMAGE_UPLOAD_HINT}</Text>
                {logoLeftPreview && (
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Preview:</Text>
                    <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 12 }}>
                      <Image
                        src={logoLeftPreview}
                        alt="left logo"
                        style={{ maxHeight: 80, maxWidth: 150, objectFit: "contain" }}
                      />
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setLogoLeftFile(null);
                          setLogoLeftPreview("");
                          setLogoLeftRemoved(true);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <Input
                    placeholder="Link URL (e.g. https://example.com)"
                    value={logoLeftUrl}
                    onChange={(e) => setLogoLeftUrl(e.target.value)}
                  />
                </div>
                <Checkbox
                  checked={defaultEmailLogo === "left"}
                  onChange={(e) => setDefaultEmailLogo(e.target.checked ? "left" : "")}
                  style={{ marginTop: 8 }}
                >
                  Default Email Logo
                </Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Right Logo">
                <Upload
                  {...siteUploadProps(setLogoRightFile, setLogoRightPreview, rightFileList)}
                >
                  <Button icon={<UploadOutlined />}>Select Right Logo</Button>
                </Upload>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>{IMAGE_UPLOAD_HINT}</Text>
                {logoRightPreview && (
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Preview:</Text>
                    <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 12 }}>
                      <Image
                        src={logoRightPreview}
                        alt="right logo"
                        style={{ maxHeight: 80, maxWidth: 150, objectFit: "contain" }}
                      />
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setLogoRightFile(null);
                          setLogoRightPreview("");
                          setLogoRightRemoved(true);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <Input
                    placeholder="Link URL (e.g. https://example.com)"
                    value={logoRightUrl}
                    onChange={(e) => setLogoRightUrl(e.target.value)}
                  />
                </div>
                <Checkbox
                  checked={defaultEmailLogo === "right"}
                  onChange={(e) => setDefaultEmailLogo(e.target.checked ? "right" : "")}
                  style={{ marginTop: 8 }}
                >
                  Default Email Logo
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left" style={{ marginTop: 24 }}>Left Toolbar</Divider>
          <div>
            <Checkbox
              checked={toolbarEnabled}
              onChange={(e) => {
                setToolbarEnabled(e.target.checked);
                if (e.target.checked && siteAvailableFiles.length === 0) loadSiteAvailableFiles();
              }}
            >
              Enable Toolbar
            </Checkbox>
            {toolbarEnabled && (
              <div style={{ marginTop: 16 }}>
                {toolbarItems.map((item, idx) => (
                  <div
                    key={item.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "8px 12px",
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      marginBottom: 8,
                      background: "#fafafa",
                    }}
                  >
                    <div style={{ width: 52, height: 52, flexShrink: 0 }}>
                      {item.imagePreview ? (
                        <Image
                          src={item.imagePreview}
                          alt="toolbar icon"
                          style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 4 }}
                          preview={false}
                        />
                      ) : (
                        <div
                          style={{
                            width: 52,
                            height: 52,
                            background: "#f0f0f0",
                            borderRadius: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PictureOutlined style={{ color: "#bbb", fontSize: 20 }} />
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Input
                        size="small"
                        placeholder="Icon name / tooltip"
                        value={item.name}
                        onChange={(e) => updateToolbarItem(idx, "name", e.target.value)}
                        style={{ marginBottom: 6 }}
                      />
                      <Upload
                        beforeUpload={(file) => {
                          updateToolbarItem(idx, "imageFile", file);
                          updateToolbarItem(idx, "imagePreview", URL.createObjectURL(file));
                          return false;
                        }}
                        maxCount={1}
                        accept="image/*"
                        showUploadList={false}
                      >
                        <Button size="small" icon={<UploadOutlined />} style={{ marginBottom: 6 }}>
                          Upload Icon
                        </Button>
                      </Upload>
                      <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: -4, marginBottom: 6 }}>{IMAGE_UPLOAD_HINT}</Text>
                      <Radio.Group
                        value={item.contentType || "html"}
                        onChange={(e) => updateToolbarItem(idx, "contentType", e.target.value)}
                        size="small"
                        style={{ marginBottom: 6, display: "block" }}
                      >
                        <Radio value="html">HTML Page</Radio>
                        <Radio value="image">Image/PDF Page</Radio>
                      </Radio.Group>
                      {(item.contentType || "html") === "html" ? (
                        <Select
                          value={item.pageFile || undefined}
                          onChange={(val) => updateToolbarItem(idx, "pageFile", val || "")}
                          placeholder="Select HTML page"
                          style={{ width: "100%" }}
                          allowClear
                          size="small"
                          onClear={() => updateToolbarItem(idx, "pageFile", "")}
                        >
                          {siteAvailableFiles.map((f) => (
                            <Select.Option key={f} value={f}>
                              {f}
                            </Select.Option>
                          ))}
                        </Select>
                      ) : (
                        <div>
                          <Upload
                            beforeUpload={(file) => {
                              updateToolbarItem(idx, "contentImageFile", file);
                              updateToolbarItem(idx, "contentImagePreview", URL.createObjectURL(file));
                              return false;
                            }}
                            maxCount={1}
                            accept="image/*,application/pdf"
                            showUploadList={false}
                          >
                            <Button size="small" icon={<UploadOutlined />}>
                              Upload Page Image or PDF
                            </Button>
                          </Upload>
                          {item.contentImagePreview && (
                            <div style={{ marginTop: 6 }}>
                              {isPdf(item.contentImageFile) || isPdf(item.contentImagePreview) ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <FilePdfOutlined style={{ fontSize: 24, color: "#cf1322" }} />
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {item.contentImageFile?.name || "PDF uploaded"}
                                  </Text>
                                </div>
                              ) : (
                                <Image
                                  src={item.contentImagePreview}
                                  alt="page content"
                                  style={{ maxWidth: 120, maxHeight: 80, objectFit: "contain", borderRadius: 4 }}
                                />
                              )}
                            </div>
                          )}
                          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                            {IMAGE_OR_PDF_UPLOAD_HINT}. This will be displayed within the site's standard header/footer layout.
                          </Text>
                        </div>
                      )}
                    </div>
                    <Space direction="vertical" size={4}>
                      <Button
                        size="small"
                        icon={<ArrowUpOutlined />}
                        onClick={() => moveToolbarItem(idx, -1)}
                        disabled={idx === 0}
                      />
                      <Button
                        size="small"
                        icon={<ArrowDownOutlined />}
                        onClick={() => moveToolbarItem(idx, 1)}
                        disabled={idx === toolbarItems.length - 1}
                      />
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeToolbarItem(idx)}
                      />
                    </Space>
                  </div>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addToolbarItem}
                  block
                  style={{ marginTop: 4 }}
                >
                  Add Icon
                </Button>
              </div>
            )}
          </div>
          <Divider orientation="left" style={{ marginTop: 24 }}>Homepage Layout</Divider>
          <Form.Item label="Homepage Type">
            <Radio.Group value={homepageType} onChange={(e) => setHomepageType(e.target.value)}>
              <Radio value="sections">Home Page Sections</Radio>
              <Radio value="single">Single Homepage</Radio>
            </Radio.Group>
          </Form.Item>
          {homepageType === "single" && (
            <>
              <Form.Item
                label={
                  <span>
                    Hero Image
                    <Text type="secondary" style={{ fontWeight: "normal", marginLeft: 8, fontSize: 12 }}>
                      (JPG, PNG, GIF, or HEIC — max 8MB — square crop recommended)
                    </Text>
                  </span>
                }
              >
                <Upload
                  beforeUpload={(file) => {
                    const maxBytes = 8 * 1024 * 1024;
                    if (file.size > maxBytes) {
                      message.error(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is 8 MB. Please resize the image before uploading.`);
                      return false;
                    }
                    setHeroImageFile(file);
                    setHeroImagePreview(URL.createObjectURL(file));
                    return false;
                  }}
                  onRemove={() => {
                    setHeroImageFile(null);
                    setHeroImagePreview("");
                  }}
                  maxCount={1}
                  accept="image/*"
                  showUploadList={false}
                  fileList={heroImageFile ? [{ uid: "hero-new", name: heroImageFile.name, status: "done" }] : heroImagePreview ? [{ uid: "hero", name: heroImagePreview.split("/").pop(), status: "done" }] : []}
                >
                  <Button icon={<UploadOutlined />}>Select Hero Image</Button>
                </Upload>
                {heroImagePreview && (
                  <div style={{ marginTop: 12 }}>
                    <Image
                      src={heroImagePreview}
                      alt="hero preview"
                      style={{ maxWidth: 240, maxHeight: 240, objectFit: "cover", borderRadius: 6, border: "1px solid #d9d9d9" }}
                    />
                  </div>
                )}
              </Form.Item>
              <Form.Item label="Homepage Text">
                <ReactQuill
                  theme="snow"
                  value={homepageText}
                  onChange={setHomepageText}
                  style={{ minHeight: 220 }}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ color: [] }, { background: [] }],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ align: [] }],
                      ["link"],
                      ["clean"],
                    ],
                  }}
                />
                <Text type="secondary" style={{ marginTop: 8, display: "block" }}>
                  This text appears to the left of the hero image on your homepage.
                </Text>
              </Form.Item>
            </>
          )}
          <Button type="primary" onClick={handleSaveSite} loading={siteLoading} disabled={disabledSiteActions} style={{ marginTop: 16 }}>
            Save Website
          </Button>
        </Form>
      </Card>

      {homepageType === "sections" && <Card
        title="Homepage Sections"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openNewSection} disabled={disabledSiteActions}>Add Section</Button>}
        loading={sectionLoading}
      >
        <Table
          dataSource={sections.map((item) => ({ ...item, key: item.id }))}
          columns={sectionColumns}
          pagination={false}
        />
      </Card>}

      <Modal
        visible={sectionModalVisible}
        title={editingSection ? "Edit Section" : "Add Section"}
        onCancel={() => {
          setSectionModalVisible(false);
          resetSectionModal();
        }}
        onOk={handleSaveSection}
        confirmLoading={sectionLoading}
        destroyOnClose
        width={infoPageEnabled ? 800 : 520}
      >
        <Form layout="vertical" form={sectionForm}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Button Text">
            <TextArea rows={3} placeholder="ie REGISTER or APPLY" />
          </Form.Item>
          <Form.Item label="Font Color" style={{ marginBottom: 0 }}>
            <Space>
              <Form.Item name="font_color" noStyle>
                <Input placeholder="#000000" style={{ width: 120 }} />
              </Form.Item>
              <input
                type="color"
                value={sectionForm.getFieldValue('font_color') || '#000000'}
                onChange={(e) => {
                  sectionForm.setFieldsValue({ font_color: e.target.value });
                }}
                style={{ width: 40, height: 32, padding: 0, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer' }}
              />
            </Space>
          </Form.Item>
          <Form.Item label="Image">
            <Upload
              beforeUpload={(file) => {
                setSectionImageFile(file);
                return false;
              }}
              onRemove={() => setSectionImageFile(null)}
              maxCount={1}
              accept="image/*"
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>Select Image</Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>{IMAGE_UPLOAD_HINT}</Text>
          </Form.Item>
          <Divider />
          <Form.Item label="External URL">
            <Space>
              <Switch
                checked={externalUrlEnabled}
                onChange={(checked) => setExternalUrlEnabled(checked)}
              />
              {externalUrlEnabled && (
                <Input
                  placeholder="https://example.com"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  style={{ width: 320 }}
                />
              )}
            </Space>
            <div>
              <Text type="secondary">
                When enabled, clicking this section opens the URL in a new tab instead of the normal registration flow.
              </Text>
            </div>
          </Form.Item>
          <Divider />
          <Form.Item label="Enable Info Page">
            <Space>
              <Switch
                checked={infoPageEnabled}
                onChange={(checked) => setInfoPageEnabled(checked)}
              />
              {infoPageEnabled && (
                <Radio.Group
                  value={infoPageType}
                  onChange={(e) => setInfoPageType(e.target.value)}
                  style={{ marginLeft: 16 }}
                >
                  <Radio value="custom">Custom</Radio>
                  <Radio value="fixed">Fixed</Radio>
                </Radio.Group>
              )}
            </Space>
            <div>
              <Text type="secondary">
                Show a custom info page before the registration list
              </Text>
            </div>
          </Form.Item>
          {infoPageEnabled && infoPageType === "fixed" && (
            <Form.Item label="Select Info Page File">
              <Select
                value={infoPageFile}
                onChange={(value) => setInfoPageFile(value)}
                placeholder="Select an HTML or PHP file"
                style={{ width: "100%" }}
                allowClear
              >
                {availableFiles.map((file) => (
                  <Select.Option key={file} value={file}>
                    {file}
                  </Select.Option>
                ))}
              </Select>
              <Text type="secondary" style={{ marginTop: 8, display: "block" }}>
                Select an existing HTML or PHP file from your subdomain directory.
              </Text>
            </Form.Item>
          )}
          {infoPageEnabled && infoPageType === "custom" && (
            <Form.Item label="Info Page Content">
              <ReactQuill
                theme="snow"
                value={infoPageContent}
                onChange={setInfoPageContent}
                style={{ minHeight: 200 }}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ color: [] }, { background: [] }],
                    [{ list: "ordered" }, { list: "bullet" }],
                    [{ align: [] }],
                    ["link", "image"],
                    ["clean"],
                  ],
                }}
              />
              <Text type="secondary" style={{ marginTop: 8, display: "block" }}>
                Create the content users will see before viewing the registration list. Include a call-to-action.
              </Text>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default CustomWebpagePage;
